import React, { useContext, useState, useEffect } from "react";
import { Card, CardBody, Container, Row, Col, Button, Spinner } from "reactstrap";
import { useParams } from "react-router-dom";
import { BreadcrumbContext } from "../../../../Layouts/Header";
import { VOLUNTEER_PATHS } from "Routes/paths";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import { formatDate, GetUserData, perPageData, showToastMessage } from "common/service/CommonService";
import styles from "../css/volunteerTest.module.scss";
import AddAlcoholTestModal from "../components/AddAlcoholTestModal";
import DeleteModal from "Components/Common/DeleteModal";
import sf from "../../../../assets/scss/common/searchFilter.module.scss";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "../../../../common/constants/PermissionConstants";
import { LatestRecord } from "../helper/volunteertest.types";
import { VolunteerTestCard } from "../helper/volunteertest.constant";
import { useVolunteerTest } from "../api/useVolunteerTest";
import { alcoholTest } from "modules/Volunteer/AssigningStudyModule/StudyTestList/helper/studytest.constant";
import { queryClient } from "api/queryClient";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal"
import { AlcoholResult_OPTIONS, loadCodelistsDetailId } from "api/modules/common.option";
import CommonButton from "Components/Common/CommonButton";

export interface StatFieldProps {
  label: string;
  value?: React.ReactNode;
  highlight?: boolean;
}

// ─────────────────────────────────────────────
// Stat Field Component
// ─────────────────────────────────────────────
const StatField: React.FC<StatFieldProps> = ({ label, value, highlight }) => (
  <div>
    <div className={styles.stat_label}>{label}</div>
    <div
      className={`${styles.stat_value} ${highlight ? styles.highlight : ""}`}
    >
      {value ?? "—"}
    </div>
  </div>
);

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
export interface VolunteerTestsProps {
  volunteerid?: number;
  readonly?: boolean;
}

const VolunteerTests: React.FC<VolunteerTestsProps> = ({ volunteerid, readonly }) => {
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [selectedTest, setSelectedTest] = useState<string | null>("alcohol");
  const [searchText, setSearchText] = useState<string | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const { id } = useParams();
  const activeVolunteerId = volunteerid ?? Number(id);

  useEffect(() => {
    loadCodelistsDetailId(["AlcoholResult_OPTIONS"])
  }, [])

  const { SelectAllVolunteerTestResult, DeleteMutation } = useVolunteerTest({
    volunteerid: activeVolunteerId,
    currentpage: currentPageNumber,
    pagesize: perPageData,
    searchtext: searchText,
    studyid: null,
  });

  const { data, isFetching } = SelectAllVolunteerTestResult as {
    data?: any;
    isFetching: boolean;
  };
  const [alcoholTestsData, setalcoholTestsData] = useState<any[]>(
    data?.data || [],
  );
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState<any>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [showReason, setShowReason] = useState(false);

  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;

  useEffect(() => {
    setBreadcrumbItems?.([
      { text: "Volunteers", link: VOLUNTEER_PATHS.LIST },
      { text: "Test Results" },
    ]);

    return () => {
      setBreadcrumbItems?.([]);
    };
  }, []);

  const handleFinalDelete = (recordId: number | string, reason: string) => {
    DeleteMutation.mutate(
      {
        alcoholtestid: recordId,
        volunteerid: activeVolunteerId,
        studyid: null,
        updatedby: GetUserData()?.userid,
        updatereason: reason,
        modulename: "Volunteer Alcohol Test"
      },
      {
        onSuccess: (res: any) => {
          queryClient.invalidateQueries({ queryKey: ["volunteer-alcohol-test-result"] });
          showToastMessage("success", res?.message || "Deleted successfully");
          setIsDeleteOpen(false);
          setShowReason(false);
        },
        onError: (err: any) => {
          showToastMessage("error", err?.message || "Delete failed");
        },
      }
    );
  };

  useEffect(() => {
    if (data?.data) {
      setalcoholTestsData(data.data);
    } else {
      setalcoholTestsData([]);
    }
  }, [data]);

  useEffect(() => {
    setCurrentPageNumber(1);
  }, []);

  const onSearch = (text: string | null) => {
    if (text !== null && text !== undefined) {
      setSearchText(text);
      setCurrentPageNumber(1);
    }
  };

  let formattedTestList: LatestRecord[] = alcoholTestsData.map((item: any) => {
    const dateObj = new Date(item.alcoholtesteddate || item.testdate);
    return {
      ...item,
      alcoholresult: item.alcoholresulttext,
      testdate: formatDate(item.alcoholtesteddate || item.testdate),
      testtime:
        item.testtime ||
        (dateObj && !isNaN(dateObj.getTime())
          ? dateObj.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
          })
          : ""),
    };
  });

  if (searchText && searchText.trim() !== "") {
    const lowerSearch = searchText.toLowerCase();
    formattedTestList = formattedTestList.filter((item) => {
      return Object.values(item).some(
        (val) => val && String(val).toLowerCase().includes(lowerSearch),
      );
    });
  }

  const latestRecord =
    formattedTestList.length > 0
      ? formattedTestList[formattedTestList.length - 1]
      : null;

  const isPassed =
    latestRecord?.alcoholresulttext?.toLowerCase() === "negative";

  const assignedStudy = data?.assignedstudy ?? data?.data?.[0]?.assignedstudy ?? null;
  const isAddDisabled = assignedStudy !== null && assignedStudy !== undefined && assignedStudy !== "";

  const [editData, setEditData] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);


  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);


  // Audit logic
  const { SelectAllMutation: AuditMutation } = useAuditService();
  const onAuditClick = () => {
    let payload = {};

    if (selectedTest === "alcohol") {
      payload = {
        modulename: "Volunteer Alcohol Test",
        refid: activeVolunteerId,
      }
    }

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Study_Lab_Test_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setPreviewUrl(null);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  return (
    <Container fluid className="px-0">
      <Row className="g-3">
        {/* ───────── Alcohol Card ───────── */}
        <Col xs={12}>
          {VolunteerTestCard.filter((t) => t.key === "alcohol").map((test) => (
            <Card
              key={test.key}
              className={styles.test_card_new}
            >
              <CardBody className="p-0 d-flex align-items-center position-relative overflow-hidden" style={{ minHeight: "140px" }}>

                {(isFetching || alcoholTestsData.length === 0) ? (
                  /* ─── SKELETON LOADER ─── */
                  <div className="d-flex align-items-center justify-content-between w-100 position-relative" style={{ maxWidth: "960px", zIndex: 2 }}>
                    {[1, 2, 3, 4].map((n) => (
                      <React.Fragment key={n}>
                        {n > 1 && (
                          <div className="vl" style={{ width: "1px", height: "4.5rem", backgroundColor: "#eff6ff", margin: "0" }} />
                        )}
                        <div className="d-flex align-items-center gap-3 px-4 py-3 placeholder-glow" style={{ width: n === 1 ? "260px" : n === 2 ? "260px" : n === 3 ? "260px" : "260px", flexShrink: 0 }}>
                          <div className="placeholder flex-shrink-0" style={{ width: n === 1 ? "56px" : "44px", height: n === 1 ? "56px" : "44px", borderRadius: n === 1 ? "16px" : "50%", backgroundColor: "#e9ecef" }} />
                          <div className="w-100">
                            <div className="placeholder col-5 mb-2" style={{ height: "10px", backgroundColor: "#e9ecef", borderRadius: "4px" }} /><br />
                            <div className="placeholder col-8 mb-2" style={{ height: "14px", backgroundColor: "#dee2e6", borderRadius: "4px" }} /><br />
                            <div className="placeholder col-4" style={{ height: "16px", backgroundColor: "#e9ecef", borderRadius: "10px" }} />
                          </div>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                ) : latestRecord ? (
                  <>
                    {/* Decorative dot pattern (alive background) */}
                    <svg
                      className={styles.dots_bg_svg}
                      width="120"
                      height="120"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <pattern id="dotPattern" x="0" y="0" width="16" height="16" patternUnits="userSpaceOnUse">
                        <circle cx="3" cy="3" r="1.5" fill="#2563eb" fillOpacity="0.08" />
                      </pattern>
                      <rect width="120" height="120" fill="url(#dotPattern)" />
                    </svg>

                    {/* Decorative animated beaker (alive background) */}
                    <svg
                      className={styles.beaker_bg_svg}
                      width="300"
                      height="220"
                      viewBox="0 0 100 100"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <defs>
                        <linearGradient id="liquidGrad" x1="0%" y1="100%" x2="0%" y2="0%">
                          <stop offset="0%" stopColor="#2563eb" stopOpacity="0.15" />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.05" />
                        </linearGradient>
                        <linearGradient id="beakerOutline" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#2563eb" stopOpacity="0.1" />
                          <stop offset="100%" stopColor="#2563eb" stopOpacity="0.02" />
                        </linearGradient>
                      </defs>

                      {/* Molecular structure behind flask */}
                      <g opacity="0.5">
                        <path
                          d="M 10 20 L 20 15 L 20 5 L 10 0 L 0 5 L 0 15 Z"
                          stroke="#2563eb"
                          strokeWidth="0.5"
                          strokeOpacity="0.08"
                          fill="none"
                          transform="translate(10, 40) scale(0.8)"
                        />
                        <path
                          d="M 10 20 L 20 15 L 20 5 L 10 0 L 0 5 L 0 15 Z"
                          stroke="#2563eb"
                          strokeWidth="0.5"
                          strokeOpacity="0.08"
                          fill="none"
                          transform="translate(26, 31) scale(0.8)"
                        />
                        <path
                          d="M 10 20 L 20 15 L 20 5 L 10 0 L 0 5 L 0 15 Z"
                          stroke="#2563eb"
                          strokeWidth="0.5"
                          strokeOpacity="0.08"
                          fill="none"
                          transform="translate(26, 49) scale(0.8)"
                        />
                      </g>

                      <path
                        d="M 38 52 L 62 52 L 72 78 A 4 4 0 0 1 68 83 L 32 83 A 4 4 0 0 1 28 78 Z"
                        fill="url(#liquidGrad)"
                      />
                      <path
                        d="M 44 18 L 44 32 L 28 76 A 5 5 0 0 0 33 83 L 67 83 A 5 5 0 0 0 72 76 L 56 32 L 56 18 M 42 18 L 58 18"
                        stroke="url(#beakerOutline)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      {/* Bubbles floating up inside liquid */}
                      <circle cx="38" cy="70" r="2" fill="#2563eb" opacity="0.25" className={styles.bubble_1} />
                      <circle cx="48" cy="62" r="2.5" fill="#2563eb" opacity="0.2" className={styles.bubble_2} />
                      <circle cx="56" cy="72" r="1.8" fill="#2563eb" opacity="0.3" className={styles.bubble_3} />
                      <circle cx="43" cy="50" r="1.2" fill="#3b82f6" opacity="0.35" className={styles.bubble_4} />
                      <circle cx="50" cy="42" r="1.8" fill="#3b82f6" opacity="0.25" className={styles.bubble_5} />
                    </svg>

                    <div className="d-flex align-items-stretch w-100 position-relative" style={{ zIndex: 2 }}>
                      {/* 1. Alcohol Test Section */}
                      <div
                        className={`${styles.section_item} d-flex align-items-center gap-3 px-4 py-3`}
                        style={{ flex: "1 1 0%", borderRight: "1px solid #dbeafe" }}
                      >
                        <div className={styles.flask_icon_wrapper}>
                          <i className="ri-flask-line fs-4" style={{ color: "#2563eb" }}></i>
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold text-dark fs-14">{test.name}</h6>
                          <span className="text-muted fs-11 fw-medium d-block" style={{ marginTop: "2px" }}>
                            Volunteer's Latest Alcohol Test Result
                          </span>
                          <div className={styles.accent_bar} />
                        </div>
                      </div>

                      {/* 2. Stage Section */}
                      <div
                        className={`${styles.section_item} d-flex align-items-center gap-3 px-4 py-3`}
                        style={{ flex: "1 1 0%", borderRight: "1px solid #dbeafe" }}
                      >
                        <div className={styles.circular_icon_wrapper}>
                          <i className="ri-flag-line fs-5" style={{ color: "#2563eb" }}></i>
                        </div>
                        <div>
                          <span className="text-muted fs-11 fw-semibold d-block">Stage</span>
                          <span className="fw-bold text-dark fs-14">{latestRecord?.alcoholstage || latestRecord?.latest_alcoholstage || alcoholTestsData[0]?.latest_alcoholstage || alcoholTestsData[0]?.alcoholstage || "—"}</span>
                          <div>
                            <span
                              className="fs-10 fw-semibold px-2 rounded-pill"
                              style={{
                                color: "#2563eb",
                                backgroundColor: "#eff6ff",
                                display: "inline-block",
                                lineHeight: "1.6",
                                marginTop: "3px",
                              }}
                            >
                              Volunteer
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* 3. BAC Value Section */}
                      <div
                        className={`${styles.section_item} d-flex align-items-center gap-3 px-4 py-3`}
                        style={{ flex: "1 1 0%", borderRight: "1px solid #dbeafe" }}
                      >
                        <div className={styles.circular_icon_wrapper}>
                          <i className="ri-drop-line fs-5" style={{ color: "#2563eb" }}></i>
                        </div>
                        <div>
                          <span className="text-muted fs-11 fw-semibold d-block">BAC Value</span>
                          <div className="d-flex align-items-baseline gap-1">
                            <span className="fw-bold fs-18" style={{ color: "#1e293b", lineHeight: "1.2" }}>
                              {alcoholTestsData[0]?.bac ?? "—"}
                            </span>
                            <span className="text-muted fs-11 fw-medium">{alcoholTestsData[0]?.alcoholunit || "% BAC"}</span>
                          </div>
                          <span className="text-muted fs-10 fw-medium d-block" style={{ marginTop: "2px" }}>Blood Alcohol Concentration</span>
                        </div>
                      </div>

                      {/* 4. Result Section */}
                      <div
                        className={`${styles.section_item} d-flex align-items-center gap-3 px-4 py-3`}
                        style={{ flex: "1 1 0%" }}
                      >
                        <div className={alcoholTestsData[0]?.alcoholresulttext === "Negative" ? styles.result_check_wrapper : styles.result_cross_wrapper}>
                          <i
                            className={alcoholTestsData[0]?.alcoholresulttext === "Negative" ? "ri-checkbox-circle-line fs-5" : "ri-close-circle-line fs-5"}
                            style={{ color: alcoholTestsData[0]?.alcoholresulttext === "Negative" ? "#16a34a" : "#dc2626" }}
                          ></i>
                        </div>
                        <div>
                          <span className="text-muted fs-11 fw-semibold d-block">Result</span>
                          <span className="fw-bold fs-16 d-block" style={{ color: alcoholTestsData[0]?.alcoholresulttext === "Negative" ? "#16a34a" : "#dc2626", lineHeight: "1.3" }}>
                            {alcoholTestsData[0]?.alcoholresulttext === "Negative" ? "Passed" : "Failed"}
                          </span>
                          <span className="text-muted fs-11 fw-medium d-block">
                            {alcoholTestsData[0]?.alcoholresulttext || "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="d-flex align-items-center justify-content-center flex-grow-1 text-muted fs-14 fw-medium py-3">
                    No records found
                  </div>
                )}
              </CardBody>
            </Card>
          ))}
        </Col>

        {/* ───────── Other Cards ───────── */}
      </Row>

      {/* ================= TABLE VIEW ================= */}
      {selectedTest && (
        <Card className="table_wrapper mt-3 border-0 shadow-sm rounded-4">
          <CardBody className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <i className="ri-list-check fs-5 text-primary"></i>
                {VolunteerTestCard.find((t) => t.key === selectedTest)?.name ||
                  "Alcohol Test"}{" "}
                Records
              </h5>

              <div
                className={`${sf.searchBox} ${searchFocused ? sf.focused : ""}`}
              >
                <i className={`ri-search-line ${sf.searchIcon}`} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchText ?? ""}
                  className={sf.searchInput}
                  onChange={(e) => onSearch(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                />
                {searchText && (
                  <i
                    className={`ri-close-line ${sf.clearIcon}`}
                    onClick={() => onSearch("")}
                  />
                )}
              </div>
              <div className="d-flex gap-2">
                <CommonButton
                  module={PERMISSION_MODULES.VOLUNTEER}
                  action={PERMISSION_ACTIONS.ALCOHOLTEST_AUDIT}
                  color="primary"
                  className="px-3"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                {!readonly && (
                  <CommonButton
                    module={PERMISSION_MODULES.VOLUNTEER}
                    action={PERMISSION_ACTIONS.ALCOHOLTEST_ADD}
                    color="primary"
                    outline
                    onClick={() => {
                      setIsAddModalOpen(true);
                    }}
                    disabled={isAddDisabled}
                  >
                    <i className="ri-add-line fs-14 size-md"></i> Add
                  </CommonButton>
                )}
              </div>
            </div>

            {alcoholTest ? (
              <CommonTableIndexWise
                moduleName={`${PERMISSION_MODULES.VOLUNTEER}:ALCOHOLTEST`}
                columns={alcoholTest}
                data={alcoholTestsData}
                actions={{
                  // onDelete: (row) => {
                  //   setDeleteRow(row);
                  //   setIsDeleteOpen(true);
                  // },
                  onEdit: (row) => {
                    setEditData(row);
                    setIsEditMode(true);
                    setIsAddModalOpen(true);
                  },
                }}
                hideAction={readonly}
                loading={isFetching}
                pageSize={5}
                currentPage={currentPageNumber}
                onPageChange={setCurrentPageNumber}
                totalRecords={data?.data?.[0]?.totalcount || 0}
              />
            ) : (
              <div className="py-5 text-center text-muted">
                <i className="ri-folder-info-line fs-1 text-secondary opacity-50 mb-3 d-block"></i>
                <h5>
                  No{" "}
                  {VolunteerTestCard.find((t) => t.key === selectedTest)
                    ?.name || "Alcohol Test"}{" "}
                  table configured yet
                </h5>
                <p>Click the add button to enter new records.</p>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* ================= ADD TEST MODAL ================= */}
      <AddAlcoholTestModal
        isOpen={isAddModalOpen}
        toggle={() => {
          setIsAddModalOpen(false);
          setIsEditMode(false);
          setEditData(null);
        }}
        editData={editData}
        isEditMode={isEditMode}
        volunteerid={activeVolunteerId}
        studyid={assignedStudy}
        alcoholstage="Volunteer"
        modulename="Volunteer Alcohol Test"
      />
      <FilePreviewModal
        file={previewFile}
        fileUrl={previewUrl}
        isOpen={isPreviewOpen}
        size={"lg"}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewFile(null);
          setPreviewUrl(null);
        }}
      />
      <DeleteModal
        isopen={isDeleteOpen}
        recordId={
          deleteRow?.alcoholtestid || deleteRow?.id || deleteRow?.studylabtestid
        }
        recordName={deleteRow?.testname || deleteRow?.bac || "Record"}
        toggle={() => setIsDeleteOpen(false)}
        showDeleteReason={showReason}
        toggleDeleteReason={() => {
          setShowReason((prev) => !prev);
          setIsDeleteOpen(false);
        }}
        onDeleteClick={({ id, reason }) => {
          handleFinalDelete(id as number | string, reason);
        }}
        isLoading={DeleteMutation.isPending}
      />
    </Container>
  );
};

export default VolunteerTests;
